import { ErrorCode, ErrorSeverity } from '../../lib/errors';

export interface CatalogEntry {
  title: string;
  message: string | ((params?: any) => string);
  severity?: ErrorSeverity;
  preferModal?: boolean;
  actions?: { label: string; action: 'retry' | 'openCorel' | 'openSettings' | 'dismiss' | 'configureSmb' | 'openHelp' }[];
}

export const ErrorCatalog: Record<ErrorCode, CatalogEntry> = {
  // WebView2 padronizados
  [ErrorCode.ENTRADA_INVALIDA]: {
    title: 'Entrada inválida',
    message: 'Verifique os dados informados e tente novamente.',
    severity: 'warning'
  },
  [ErrorCode.DOCUMENTO_INATIVO]: {
    title: 'Documento/Corel indisponível',
    message: 'Abra um documento no CorelDraw e tente novamente.',
    severity: 'error',
    preferModal: true,
    actions: [
      { label: 'Abrir Corel', action: 'openCorel' },
      { label: 'Tentar novamente', action: 'retry' }
    ]
  },
  [ErrorCode.FORMATO_NAO_SUPORTADO]: {
    title: 'Formato não suportado',
    message: 'O formato solicitado não é suportado pela ação.',
    severity: 'error'
  },
  [ErrorCode.PATH_INVALIDO]: {
    title: 'Caminho inválido',
    message: 'O caminho informado está inválido. Corrija e tente novamente.',
    severity: 'error'
  },
  [ErrorCode.PATH_SISTEMA_NEGADO]: {
    title: 'Acesso negado pelo sistema',
    message: 'Permissões insuficientes para acessar o caminho.',
    severity: 'error',
    preferModal: true,
    actions: [
      { label: 'Abrir configurações', action: 'openSettings' },
      { label: 'Tentar novamente', action: 'retry' }
    ]
  },
  [ErrorCode.PATH_NAO_AUTORIZADO]: {
    title: 'Caminho não autorizado',
    message: 'O caminho está bloqueado pela política de segurança.',
    severity: 'error',
    preferModal: true
  },
  [ErrorCode.CRIAR_DIRETORIO_FALHOU]: {
    title: 'Erro ao criar diretório',
    message: 'Não foi possível criar o diretório de destino.',
    severity: 'error'
  },
  [ErrorCode.ARQUIVO_JA_EXISTE]: {
    title: 'Arquivo já existe',
    message: 'Um arquivo com o mesmo nome já existe no destino.',
    severity: 'warning'
  },
  [ErrorCode.SMB_CREDENCIAIS_AUSENTES]: {
    title: 'Credenciais SMB ausentes',
    message: 'Preencha usuário e senha para acesso ao compartilhamento de rede.',
    severity: 'warning',
    actions: [
      { label: 'Configurar SMB', action: 'configureSmb' },
      { label: 'Tentar novamente', action: 'retry' }
    ]
  },
  [ErrorCode.SMB_AUTENTICACAO_FALHOU]: {
    title: 'Falha na autenticação SMB',
    message: 'Verifique as credenciais de rede e tente novamente.',
    severity: 'error',
    preferModal: true,
    actions: [
      { label: 'Configurar SMB', action: 'configureSmb' },
      { label: 'Tentar novamente', action: 'retry' }
    ]
  },
  [ErrorCode.SMB_CONEXAO_FALHOU]: {
    title: 'Conexão SMB falhou',
    message: 'Rede ou servidor inacessível. Verifique a conexão.',
    severity: 'error',
    actions: [
      { label: 'Abrir ajuda', action: 'openHelp' },
      { label: 'Tentar novamente', action: 'retry' }
    ]
  },
  [ErrorCode.IO_ACESSO_NEGADO]: {
    title: 'Acesso ao arquivo negado',
    message: 'Permissões insuficientes de leitura/escrita.',
    severity: 'error'
  },
  [ErrorCode.IO_DISCO_CHEIO]: {
    title: 'Disco cheio',
    message: 'Libere espaço no disco e tente novamente.',
    severity: 'error',
    preferModal: true
  },
  [ErrorCode.TIMEOUT]: {
    title: 'Tempo esgotado',
    message: 'A operação demorou mais que o esperado.',
    severity: 'warning',
    actions: [
      { label: 'Tentar novamente', action: 'retry' }
    ]
  },
  [ErrorCode.INTEGRIDADE_DIVERGENTE]: {
    title: 'Integridade divergente',
    message: 'O hash do arquivo diverge do esperado.',
    severity: 'warning',
    preferModal: true,
    actions: [
      { label: 'Tentar novamente', action: 'retry' }
    ]
  },
  [ErrorCode.USUARIO_CANCELOU]: {
    title: 'Operação cancelada',
    message: 'A operação foi cancelada pelo usuário.',
    severity: 'info'
  },
  [ErrorCode.ITEM_INVALIDO]: {
    title: 'Item inválido',
    message: 'O item/objeto informado está inválido ou não foi encontrado.',
    severity: 'error'
  },
  [ErrorCode.TARGETS_OBSOLETOS]: {
    title: 'Targets obsoletos',
    message: 'O formato de targets utilizado é obsoleto.',
    severity: 'warning'
  },
  [ErrorCode.COREL_OPEN_FAILED]: {
    title: 'Não foi possível abrir o arquivo',
    message: 'Verifique o Corel/WebView e tente novamente.',
    severity: 'error',
    preferModal: true,
  },
  [ErrorCode.WEBVIEW_UNAVAILABLE]: {
    title: 'Corel/WebView indisponível',
    message: 'Abra o CorelDraw e confirme que o WebView está operacional.',
    severity: 'error',
    preferModal: true,
  },
  [ErrorCode.LAUNCHER_NOT_READY]: {
    title: 'Sistema Inicializando',
    message: 'O sistema está inicializando. Aguarde alguns segundos e tente novamente.',
    severity: 'warning',
    preferModal: false,
    actions: [
      { label: 'Tentar novamente', action: 'retry' }
    ]
  },
  [ErrorCode.FILE_NOT_FOUND]: {
    title: 'Arquivo não encontrado',
    message: (p?: any) => `O arquivo esperado não foi localizado${p?.path ? `: ${p.path}` : ''}.`,
    severity: 'warning',
  },
  [ErrorCode.HASH_MISMATCH]: {
    title: 'Arquivo alterado',
    message: 'A integridade do arquivo não corresponde ao último hash salvo.',
    severity: 'warning',
    preferModal: true,
  },
  [ErrorCode.PATH_INACCESSIBLE]: {
    title: 'Caminho inacessível',
    message: 'Não foi possível acessar o diretório de produção. Verifique permissões/rede.',
    severity: 'error',
  },
  [ErrorCode.SMB_UNAUTHORIZED]: {
    title: 'Acesso negado',
    message: 'As credenciais SMB não foram aceitas. Revalide o acesso.',
    severity: 'error',
    preferModal: true,
  },
  [ErrorCode.OPEN_TIMEOUT]: {
    title: 'Tempo esgotado',
    message: 'O Corel demorou para responder. Tente novamente.',
    severity: 'warning',
  },
  [ErrorCode.API_UNAVAILABLE]: {
    title: 'Servidor indisponível',
    message: 'Não foi possível contatar a API no momento.',
    severity: 'error',
  },
  [ErrorCode.API_TIMEOUT]: {
    title: 'Tempo esgotado',
    message: 'A requisição demorou mais que o esperado.',
    severity: 'warning',
  },
  [ErrorCode.API_4XX]: {
    title: 'Solicitação inválida',
    message: 'Houve um problema com a requisição. Revise os dados e tente novamente.',
    severity: 'error',
  },
  [ErrorCode.API_5XX]: {
    title: 'Erro no servidor',
    message: 'Falha interna ao processar a requisição. Tente novamente mais tarde.',
    severity: 'critical',
    preferModal: true,
  },
  // API business codes - Metragem validation
  [ErrorCode.DIMENSION_LIMIT_EXCEEDED]: {
    title: 'Dimensões fora do limite',
    message: (p?: any) => {
      const objects = p?.details?.objects || p?.objects;
      if (Array.isArray(objects) && objects.length > 0) {
        const first = objects[0];
        const widthMax = first?.largura_maxima_cm;
        const heightMax = first?.altura_maxima_cm;
        if (typeof widthMax === 'number' && typeof heightMax === 'number') {
          return `Há objeto(s) acima do limite permitido de ${widthMax}cm x ${heightMax}cm. Ajuste as dimensões antes de salvar.`;
        }
      }
      return 'Há objeto(s) acima do limite permitido para este produto. Ajuste as dimensões antes de salvar.';
    },
    severity: 'error',
    preferModal: true,
  },
  [ErrorCode.METRAGEM_EXCEEDED]: {
    title: 'Metragem excede o vendido',
    message: (p?: any) => {
      const details = p?.details;
      if (details) {
        const label = details.umdId === 3 ? 'Metragem' : 'Quantidade';
        const unit = details.unidade || (details.umdId === 3 ? 'm' : 'un');
        return `${label} dos objetos (${details.totalCalculado}${unit}) excede o vendido (${details.metrosVendidos}${unit}). Excesso de ${details.excesso}${unit}. Reduza objetos ou ajuste o pedido.`;
      }
      return 'A metragem/quantidade dos objetos excede o limite do pedido. Reduza objetos ou ajuste o pedido.';
    },
    severity: 'error',
    preferModal: true,
  },
  [ErrorCode.WAW_CONFLICT]: {
    title: 'Arquivo já existe',
    message: 'Já existe um arquivo registrado para este item. Confirme substituição no fluxo.',
    severity: 'warning',
    preferModal: true,
    actions: [ { label: 'Tentar novamente', action: 'retry' } ]
  },
  [ErrorCode.MD5_CONFLICT]: {
    title: 'Arquivo diferente detectado',
    message: 'O arquivo enviado difere do já gravado. Confirme substituição no fluxo.',
    severity: 'warning',
    preferModal: true,
    actions: [ { label: 'Tentar novamente', action: 'retry' } ]
  },
  [ErrorCode.FK_ORCAMENTO_NOT_FOUND]: {
    title: 'Orçamento não encontrado',
    message: 'Verifique o orc_id informado.',
    severity: 'error'
  },
  [ErrorCode.FK_OIT_NOT_FOUND]: {
    title: 'Item do orçamento não encontrado',
    message: 'Verifique o oit_id informado.',
    severity: 'error'
  },
  [ErrorCode.FK_PRODUTO_NOT_FOUND]: {
    title: 'Produto não encontrado',
    message: 'Verifique o pro_id informado.',
    severity: 'error'
  },
  [ErrorCode.FK_PRODUTO_RULE_NOT_FOUND]: {
    title: 'Regra do produto não encontrada',
    message: 'Não foi localizada uma regra ativa do produto para validar o salvamento.',
    severity: 'error'
  },
  [ErrorCode.LAYOUT_ITEM_NOT_FOUND]: {
    title: 'Layout não encontrado',
    message: 'É necessário exportar PNG antes de enviar o layout.',
    severity: 'warning'
  },
  [ErrorCode.VALIDATION_ERROR]: {
    title: 'Dados inválidos',
    message: 'Existem campos inválidos ou faltando.',
    severity: 'warning',
  },
  [ErrorCode.EXPORT_REGISTER_FAILED]: {
    title: 'Falha ao Registrar Exportação',
    message: 'Não foi possível registrar a exportação no sistema após múltiplas tentativas.',
    severity: 'error',
    preferModal: false,
  },
  [ErrorCode.EXPORT_STAGE_UPDATE_FAILED]: {
    title: 'Falha ao Atualizar Estágio',
    message: 'Não foi possível mover o pedido para o próximo estágio de produção.',
    severity: 'error',
    preferModal: false,
  },
  [ErrorCode.EXPORT_LAYOUT_FAILED]: {
    title: 'Falha na Exportação de Layout',
    message: 'Não foi possível exportar os arquivos PNG de layout.',
    severity: 'warning',
    preferModal: false,
  },
  [ErrorCode.EXPORT_LAYOUT_REGISTER_FAILED]: {
    title: 'Layout Não Registrado',
    message: 'Os arquivos PNG foram gerados, mas não foi possível registrá-los no sistema.',
    severity: 'warning',
    preferModal: false,
  },
  [ErrorCode.EXPORT_MISSING_OST_ID]: {
    title: 'Status do Pedido Não Encontrado',
    message: 'O status do pedido (ost_id) não foi encontrado. Verifique se o pedido está carregado corretamente.',
    severity: 'error',
    preferModal: false,
  },
  [ErrorCode.EXPORT_MISSING_USER_ID]: {
    title: 'Usuário Não Identificado',
    message: 'Não foi possível identificar o usuário logado. Faça login novamente.',
    severity: 'error',
    preferModal: true,
  },
  [ErrorCode.UNKNOWN]: {
    title: 'Erro desconhecido',
    message: 'Ocorreu um erro inesperado.',
    severity: 'error',
  },
  // Tools API specific errors
  [ErrorCode.TOOL_NOT_FOUND]: {
    title: 'Ferramenta não encontrada',
    message: 'A ferramenta solicitada não foi encontrada ou não está disponível.',
    severity: 'error',
  },
  [ErrorCode.COMMAND_NOT_FOUND]: {
    title: 'Comando não encontrado',
    message: 'O comando solicitado não foi encontrado ou foi removido.',
    severity: 'error',
  },
  [ErrorCode.CLIENTS_NOT_FOUND]: {
    title: 'Clientes não encontrados',
    message: 'Nenhum cliente encontrado com os critérios de busca.',
    severity: 'warning',
  },
  [ErrorCode.PRODUCT_NOT_FOUND]: {
    title: 'Produto não encontrado',
    message: 'Produto não encontrado. Verifique o ID e tente novamente.',
    severity: 'error',
  },
  [ErrorCode.QUALITIES_NOT_FOUND]: {
    title: 'Qualidades não encontradas',
    message: 'Nenhuma qualidade encontrada para esta categoria.',
    severity: 'warning',
  },
  [ErrorCode.RULES_NOT_FOUND]: {
    title: 'Regras não encontradas',
    message: 'Nenhuma regra encontrada para este produto.',
    severity: 'warning',
  },
  [ErrorCode.CONFIG_NOT_FOUND]: {
    title: 'Configuração não encontrada',
    message: 'Configuração do PlotCalc não encontrada para este tenant.',
    severity: 'error',
  },
  [ErrorCode.NO_SERIAL_AVAILABLE]: {
    title: 'Serial não disponível',
    message: 'Nenhum serial do PlotCalc disponível. Entre em contato com o suporte.',
    severity: 'critical',
    preferModal: true,
  },
  [ErrorCode.TENANT_REQUIRED]: {
    title: 'Tenant não identificado',
    message: 'Tenant não identificado. Verifique a configuração e tente novamente.',
    severity: 'error',
  },
  [ErrorCode.BAD_GATEWAY]: {
    title: 'Serviço temporariamente indisponível',
    message: 'O serviço externo está temporariamente indisponível. Tente novamente em alguns instantes.',
    severity: 'warning',
    actions: [{ label: 'Tentar novamente', action: 'retry' }],
  },
  [ErrorCode.GATEWAY_TIMEOUT]: {
    title: 'Tempo limite excedido',
    message: 'O serviço externo não respondeu a tempo. O cálculo está demorando mais que o esperado.',
    severity: 'warning',
    actions: [{ label: 'Tentar novamente', action: 'retry' }],
  },
  [ErrorCode.NO_DISCOUNT_FOUND]: {
    title: 'Desconto não disponível',
    message: 'Nenhum desconto disponível para este cliente/produto.',
    severity: 'info',
  },
  [ErrorCode.CATEGORIA_ERROR]: {
    title: 'Erro ao carregar catálogo',
    message: 'Erro ao carregar catálogo. Verifique os parâmetros e tente novamente.',
    severity: 'error',
  },
  [ErrorCode.REGRAS_ERROR]: {
    title: 'Erro ao carregar regras',
    message: 'Erro ao carregar regras de salvamento. Tente novamente.',
    severity: 'error',
  },
  // Oneprint API errors - Autenticação (401)
  [ErrorCode.USER_NOT_FOUND]: {
    title: 'Usuário não encontrado',
    message: 'Usuário não encontrado no sistema. Verifique as credenciais.',
    severity: 'error',
  },
  [ErrorCode.USER_ID_REQUIRED]: {
    title: 'Token inválido',
    message: 'Token inválido ou expirado. Faça login novamente.',
    severity: 'error',
    preferModal: true,
  },
  // Oneprint API errors - Validação (400)
  [ErrorCode.MISSING_REQUIRED_FIELDS]: {
    title: 'Campos obrigatórios ausentes',
    message: 'Alguns campos obrigatórios não foram preenchidos.',
    severity: 'warning',
  },
  [ErrorCode.INVALID_IMAGES]: {
    title: 'Imagens inválidas',
    message: 'As imagens fornecidas são inválidas ou estão corrompidas.',
    severity: 'error',
  },
  [ErrorCode.INVALID_MD5]: {
    title: 'Hash MD5 inválido',
    message: 'O hash MD5 fornecido não corresponde ao arquivo.',
    severity: 'error',
  },
  [ErrorCode.INVALID_PAYLOAD]: {
    title: 'Dados inválidos',
    message: 'Os dados enviados estão em formato inválido.',
    severity: 'error',
  },
  [ErrorCode.INVALID_KEY]: {
    title: 'Chave inválida',
    message: 'A chave fornecida é inválida ou não foi reconhecida.',
    severity: 'error',
  },
  [ErrorCode.INVALID_PERM_ID]: {
    title: 'ID de permissão inválido',
    message: 'O ID de permissão fornecido é inválido.',
    severity: 'error',
  },
  [ErrorCode.INVALID_BODY]: {
    title: 'Corpo da requisição inválido',
    message: 'O corpo da requisição está em formato inválido.',
    severity: 'error',
  },
  [ErrorCode.INVALID_QUERY]: {
    title: 'Parâmetros de query inválidos',
    message: 'Os parâmetros de query fornecidos são inválidos.',
    severity: 'warning',
  },
  [ErrorCode.MISSING_PARAMETERS]: {
    title: 'Parâmetros obrigatórios ausentes',
    message: 'Alguns parâmetros obrigatórios não foram fornecidos na query.',
    severity: 'warning',
  },
  [ErrorCode.EMPTY_UPDATES]: {
    title: 'Nenhuma atualização fornecida',
    message: 'Nenhuma configuração foi fornecida para atualização.',
    severity: 'info',
  },
  // Oneprint API errors - Conflito (409)
  [ErrorCode.PEDIDO_JA_SALVO]: {
    title: 'Pedido já salvo',
    message: 'Este pedido já foi salvo anteriormente.',
    severity: 'warning',
  },
  // Oneprint API errors - Tenant (400)
  [ErrorCode.TENANT_INVALID]: {
    title: 'Tenant inválido',
    message: 'O tenant fornecido é inválido ou não foi encontrado.',
    severity: 'error',
  },
  // Oneprint API errors - Autorização (403)
  [ErrorCode.UNAUTHORIZED_403]: {
    title: 'Acesso não autorizado',
    message: 'Você não tem permissão para realizar esta ação.',
    severity: 'error',
    preferModal: true,
  },
  // Oneprint API errors - Não encontrado (404)
  [ErrorCode.ITEM_NOT_FOUND]: {
    title: 'Item não encontrado',
    message: 'O item solicitado não foi encontrado.',
    severity: 'error',
  },
  [ErrorCode.ORCAMENTO_NOT_FOUND]: {
    title: 'Orçamento não encontrado',
    message: 'O orçamento solicitado não foi encontrado.',
    severity: 'error',
  },
  // Oneprint API errors - Servidor (500)
  [ErrorCode.INTERNAL_ERROR]: {
    title: 'Erro interno do servidor',
    message: 'Ocorreu um erro interno no servidor. Tente novamente mais tarde.',
    severity: 'critical',
    preferModal: true,
  },
  [ErrorCode.UNEXPECTED_ERROR]: {
    title: 'Erro inesperado',
    message: 'Ocorreu um erro inesperado. Tente novamente.',
    severity: 'error',
  },
  [ErrorCode.UNSPECIFIED]: {
    title: 'Erro não especificado',
    message: 'Ocorreu um erro não especificado. Tente novamente.',
    severity: 'error',
  },
  [ErrorCode.ER_BAD_FIELD_ERROR]: {
    title: 'Erro de campo no banco de dados',
    message: 'Erro ao processar campo no banco de dados. Verifique os dados e tente novamente.',
    severity: 'error',
  },
  // Erros Prisma
  [ErrorCode.P2002]: {
    title: 'Registro duplicado',
    message: 'Já existe um registro com estes dados. Verifique e tente novamente.',
    severity: 'warning',
  },
  [ErrorCode.P2003]: {
    title: 'Referência inválida',
    message: 'A referência fornecida não existe no banco de dados.',
    severity: 'error',
  },
  [ErrorCode.P2021]: {
    title: 'Tabela não encontrada',
    message: 'A tabela solicitada não foi encontrada no banco de dados.',
    severity: 'error',
  },
};


